import { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const NavBarBlockContainer = styled.div`
  width: 100%;
`;

const StickyNavBar = styled.nav`
  display: flex;
  position: sticky;
  top: ${legacyTheme.props.headerHeight};
  z-index: 1000;
  height: ${props => props.height}px;
  padding: 0 27px;
  justify-content: center;

  background-color: ${theme.colors.neutral[100]};
`;

const NavBarMenu = styled.nav`
  display: flex;
  width: ${props => (props.isMobile ? '100%' : '40%')};
  justify-content: space-between;
  align-items: center;
`;

const NavBarAnchor = styled.a`
  flex: 0;
  text-decoration: underline;
  text-underline-offset: 8px;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;

  ${props => (props.isMobile ? '' : '&:hover,')}
  &.selected {
    text-decoration: underline;
    text-decoration-color: ${theme.colors.primary[400]};
    transition-duration: 1s;
  }
`;

const NavBarTypography = styled(Typography)`
  font-size: 18px;
`;

const Separator = styled(Typography)`
  flex: 1;
  font-size: 18px;
  text-align: center;
`;

const StickyNavBarBlock = ({
  children,
  sectionsInfo,
  secionInViewId,
  navBarSeparator,
  navbarHeight,
  scrollTimeInMs,
}) => {
  const { isMobile } = useResponsiveVariant();
  const [lastClickedSectionId, setLastClickedSectionId] = useState(undefined);
  const timeoutRef = useRef();

  useEffect(() => {
    /* 
      This is needed to avoid flickering between the menu sections after clicking in one of them.
      1000ms is the default value for waiting the auto-scrolling to finish, but if the page has
      many sections this value might have to be increased.
    */
    if (lastClickedSectionId) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setLastClickedSectionId(undefined), scrollTimeInMs);
    }
  }, [lastClickedSectionId]);

  /*
    if there was a user click in one the menu sections, underline that section for the duration of the timout,
    then once the timeout is over, underline the section that is in view.
  */
  const selectedSectionId = lastClickedSectionId ?? secionInViewId;

  return (
    <NavBarBlockContainer>
      <StickyNavBar height={navbarHeight}>
        <NavBarMenu isMobile={isMobile}>
          {sectionsInfo.map((section, index) => (
            <Fragment key={section.id}>
              <NavBarAnchor
                key={section.id + selectedSectionId}
                className={classNames({
                  selected: selectedSectionId === section.id,
                })}
                data-click={section.label}
                data-from="secondary-navbar"
                href={`#${section.id}`}
                isMobile={isMobile}
                onClick={() => setLastClickedSectionId(section.id)}
              >
                <NavBarTypography color="vert" medium={isMobile} upperCase variant="mono1">
                  {section.label}
                </NavBarTypography>
              </NavBarAnchor>
              {navBarSeparator && index !== sectionsInfo.length - 1 && (
                <Separator color="vert" upperCase variant="mono1">
                  {navBarSeparator}
                </Separator>
              )}
            </Fragment>
          ))}
        </NavBarMenu>
      </StickyNavBar>
      {children}
    </NavBarBlockContainer>
  );
};

StickyNavBarBlock.propTypes = {
  children: PropTypes.node.isRequired,
  sectionsInfo: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  secionInViewId: PropTypes.string,
  navBarSeparator: PropTypes.string,
  navbarHeight: PropTypes.number,
  scrollTimeInMs: PropTypes.number,
};

StickyNavBarBlock.defaultProps = {
  navBarSeparator: '',
  secionInViewId: undefined,
  navbarHeight: 64,
  scrollTimeInMs: 1000,
};

export default StickyNavBarBlock;
